import React, { useState } from "react";
import { useNavigate } from "react-router";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import EditIcon from "@mui/icons-material/Edit";
import useAuth from "src/hooks/useAuth";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import ScanOrEnterModal from "src/views/inventory/requests/orders/ScanOrEnterModal";
import ScanOutModal from "src/views/inventory/requests/orders/ScanOutModal";
import EditPickLocationsModal from "../../../EditPickLocationsModal";
import {
  UPDATE_INVENTORY_ITEM_MUTATION,
  ADD_INVENTORY_ITEM_MUTATION,
  DELETE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_REQUEST_MUTATION,
} from "src/constants/graphql/mutations";
import { INVENTORY_REQUEST_QUERY } from "src/constants/graphql/queries";
import { getInventoryFields } from "src/views/inventory/requests/CartItemFields";
import CartItemQuantity from "src/views/inventory/requests/CartItemQuantity";
import { formatRackLocationLabel } from "src/utils/rackLocations";

export default function PickingSection({
  asset,
  internalWorkOrderItem,
  orderId,
  inventoryRequest,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = user.company;
  const [openScanOrEnterModal, setOpenScanOrEnterModal] = useState(false);
  const [openScanModal, setOpenScanModal] = useState(false);
  const [openEditLocations, setOpenEditLocations] = useState(false);
  const rackLocations = internalWorkOrderItem?.rackLocations || [];
  const formattedPickLocations = rackLocations.map(rl => formatRackLocationLabel(rl, { includeQty: true })).join("\n");
  const canEditLocations = asset?.rackLocations?.length > 1;

  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION, {
    refetchQueries: [{ query: INVENTORY_REQUEST_QUERY, variables: { id: orderId } }],
  });
  const { notifySuccess } = useNotification();

  const handleQuantityChange = async newQuantity => {
    try {
      const closeParams = new URLSearchParams(window.location.search);
      closeParams.delete("inventoryItemId");
      closeParams.delete("assetId");
      closeParams.set("recreatingItem", "1");
      navigate({ search: closeParams.toString() });

      await deleteInventoryItem({ variables: { id: internalWorkOrderItem.id } });

      const fields = getInventoryFields({
        ...internalWorkOrderItem,
        assetId: asset?.id || internalWorkOrderItem.assetId,
      });

      await addInventoryItem({
        variables: {
          ...fields,
          quantity: newQuantity,
          inventoryRequestId: orderId,
        },
        onSuccess: ({ inventoryItem }) => {
          const newItemId = inventoryItem?.id;
          if (newItemId) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set("inventoryItemId", newItemId);
            if (asset?.id) searchParams.set("assetId", asset.id);
            searchParams.delete("recreatingItem");
            navigate({ search: searchParams.toString() });
          }
        },
      });
    } catch (error) {
      console.error("Error recreating item with new quantity:", error);
    }
  };

  const checkAndUpdateRequestStatus = async () => {
    if (!inventoryRequest) return;
    const allItemsPicked = inventoryRequest.inventoryItems.every(invItem =>
      invItem.id === internalWorkOrderItem.id ? true : invItem.pickedStatus
    );
    if (allItemsPicked) {
      await updateInventoryRequest({
        variables: {
          id: inventoryRequest.id,
          input: {
            vaStatus: {
              picked: true,
            },
          },
        },
      });
    }
  };
  const markAsPicked = async () => {
    try {
      await updateItem({
        variables: {
          id: internalWorkOrderItem.id,
          assetId: internalWorkOrderItem.assetId,
          quantity: internalWorkOrderItem.quantity,
          pickedStatus: true,
        },
      });
      await checkAndUpdateRequestStatus();
      notifySuccess("Item picked successfully");
    } catch (error) {
      console.error("Error marking item as picked:", error);
    }
  };

  const handleClick = () => {
    if (settings?.scan_out_modal) {
      setOpenScanOrEnterModal(true);
    } else {
      markAsPicked();
    }
  };

  const handleEditLocationsClose = newItem => {
    setOpenEditLocations(false);
    if (newItem && newItem.id) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("inventoryItemId");
      searchParams.delete("assetId");
      navigate({ search: searchParams.toString() });
      setTimeout(() => {
        searchParams.set("inventoryItemId", newItem.id);
        searchParams.set("assetId", asset.id);
        navigate({ search: searchParams.toString() });
      });
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box mb={1}>
            <Typography fontWeight={500} fontSize={18}>
              Picking From
            </Typography>
          </Box>
          <Box mb={2}>
            <CartItemQuantity
              disabled={!!internalWorkOrderItem.pickedStatus}
              value={internalWorkOrderItem?.quantity}
              availableQuantity={asset?.availableQuantity}
              onChange={handleQuantityChange}
              inputProps={{ sx: {}, fullWidth: true, label: "Quantity Requested" }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              fullWidth
              multiline
              disabled
              size="small"
              variant="outlined"
              label="Picking From"
              value={formattedPickLocations}
              InputProps={{
                readOnly: true,
                endAdornment: canEditLocations ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setOpenEditLocations(true)} size="small">
                      <EditIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
          {internalWorkOrderItem.pickedStatus ? (
            <Box bgcolor="#D5F5D5" py={1} borderRadius={1} textAlign="center" fontWeight={500}>
              Item Picked
            </Box>
          ) : (
            <Button fullWidth variant="outlined" color="primary" onClick={handleClick}>
              Pick Item
            </Button>
          )}
        </CardContent>
      </Card>

      {openScanOrEnterModal && (
        <ScanOrEnterModal
          asset={asset}
          onClose={() => setOpenScanOrEnterModal(false)}
          openScanModal={() => setOpenScanModal(true)}
          markAsPicked={async () => {
            await markAsPicked();
            setOpenScanOrEnterModal(false);
          }}
          enableSuccessNotification={false}
        />
      )}

      {openScanModal && (
        <ScanOutModal
          asset={asset}
          onClose={() => setOpenScanModal(false)}
          onScanComplete={async () => {
            setOpenScanModal(false);
            markAsPicked();
          }}
        />
      )}
      {openEditLocations && (
        <EditPickLocationsModal
          asset={asset}
          item={internalWorkOrderItem}
          rackLocations={rackLocations}
          inventoryRequest={inventoryRequest}
          onClose={handleEditLocationsClose}
        />
      )}
    </>
  );
}
